import { PaletteMode } from "@mui/material";

export const hexRegex = /#[a-fA-F0-9]{6}/gm;
export const rgbRegex = /rgb\(\d{1,3},\d{1,3},\d{1,3}\)/gm;
const CORRECTION_THRESHOLD = 0.45;
type ColorType = "rgb" | "hex";
const ColorToHSL = (
  type: ColorType,
  color: string
): { hue: number; saturation: number; lightness: number } => {
  let red = 0,
    green = 0,
    blue = 0;
  if (type === "hex") {
    let str = color.replace("#", "");
    red = parseInt(str.substring(0, 2), 16);
    green = parseInt(str.substring(2, 4), 16);
    blue = parseInt(str.substring(4, 6), 16);
  }
  if (type === "rgb") {
    let str = color.match(/\(.*\)/)?.[0].split(",") ?? [];
    red = parseInt(str[0]);
    green = parseInt(str[1]);
    blue = parseInt(str[2]);
  }

  let [r, g, b] = [red / 255, green / 255, blue / 255];
  let cmax = Math.max(r, g, b);
  let cmin = Math.min(r, g, b);
  let delta = cmax - cmin;

  let hue;
  let saturation;
  let lightness = (cmax + cmin) / 2;

  if (delta === 0) {
    hue = 0;
    saturation = 0;
  } else {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (cmax === r) hue = ((g - b) / delta) % 6;
    else if (cmax === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue *= 60;
  }
  return { hue, saturation, lightness };
};

const HSLtoHEX = (
  hue: number,
  saturation: number,
  lightness: number
): string => {
  const c = saturation * (1 - Math.abs(2 * lightness - 1));
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lightness - c / 2;
  const C = Math.floor((c + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const X = Math.floor((x + m) * 255)
    .toString(16)
    .padStart(2, "0");
  const M = Math.floor(m * 255)
    .toString(16)
    .padStart(2, "0");

  const matrice = {
    red: [C, X, M, M, X, C],
    green: [X, C, C, X, M, M],
    blue: [M, M, X, C, C, X],
  };
  const index = Math.floor(hue / 60);

  return `#${matrice.red[index]}${matrice.green[index]}${matrice.blue[index]}`;
};
export const colorCorrection = (
  type: ColorType,
  value: string,
  mode: PaletteMode = "dark"
) => {
  let { hue, saturation, lightness } = ColorToHSL(type, value);
  switch (mode) {
    case "dark":
      if (lightness > CORRECTION_THRESHOLD) return value;
      lightness += (1 - lightness) / 2;
      break;
    case "light":
      if (lightness < CORRECTION_THRESHOLD) return value;
      lightness -= lightness / 2;
      break;
  }

  return HSLtoHEX(hue, saturation, lightness);
};
// Assumes there's absolutely no reason this would be used in the actual text presented to the user
const styleRegex =
  /color:\s?(#([a-fA-F0-9]{6})|rgb\(\d{1,3},\d{1,3},\d{1,3}\))(;|")/g;
export const colorCorrectHTML = (html: string, mode: PaletteMode = "dark") => {
  let temp = html;
  (html.match(styleRegex) ?? []).forEach((color) => {
    const hex = color.match(hexRegex)?.[0];
    const rgb = color.match(rgbRegex)?.[0];

    if (hex !== undefined) {
      let newColor = color.replace(hex, colorCorrection("hex", hex, mode));
      temp = temp.replace(color, newColor);
    }
    if (rgb !== undefined) {
      let newColor = color.replace(rgb, colorCorrection("rgb", rgb, mode));
      temp = temp.replace(color, newColor);
    }
  });
  return temp;
};
